import cx from 'classnames'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import Avatar from 'common/Avatar/Avatar'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { PostLocation, PostTag, ScheduledPostTarget } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { getInitials } from 'utils'
import { LocationPicker } from './LocationPicker'
import { MentionableTextarea } from './MentionableTextarea'
import { MAX_INSTAGRAM_TAGS, MAX_X_TAGS, TagPicker } from './TagPicker'
import Styles from './EditCaptionModal.module.scss'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const MAX_TAGS_BY_PROVIDER: Partial<Record<ExternalIntegrationEnum, number>> = {
  [ExternalIntegrationEnum.Instagram]: MAX_INSTAGRAM_TAGS,
  [ExternalIntegrationEnum.X]: MAX_X_TAGS,
}

export const DYNAMIC_CAPTION_PROVIDERS = [
  ExternalIntegrationEnum.Instagram,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.YouTube,
  ExternalIntegrationEnum.TikTok,
  ExternalIntegrationEnum.GoogleBusinessProfile,
] as const

export const LOCATION_SUPPORTED_PROVIDERS: ReadonlySet<ExternalIntegrationEnum> = new Set([
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.Instagram,
])

export const TAGS_SUPPORTED_PROVIDERS: ReadonlySet<ExternalIntegrationEnum> = new Set([
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.Instagram,
])

export const MENTIONS_SUPPORTED_PROVIDERS: ReadonlySet<ExternalIntegrationEnum> = new Set([
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.Instagram,
])

export type DynamicCaptionProvider = (typeof DYNAMIC_CAPTION_PROVIDERS)[number]

export type DynamicCaptionAccountItem = {
  key: number | string
  target?: ScheduledPostTarget
  accountName: string
  accountAvatar: string | null
  accountType?: 'page' | 'profile' | 'analytics' | 'drive'
  isIntegrationActive?: boolean
}

export type DynamicCaptionRow = {
  provider: DynamicCaptionProvider
  label: string
  description: string
  targets: ScheduledPostTarget[]
  accountCaptionItems: DynamicCaptionAccountItem[]
  initialCaption: string
  isConnected: boolean
  isEnabled: boolean
  hasAccountIcon: boolean
}

interface CharacterStats {
  count: number
  limit: number
  isOverLimit: boolean
}

interface DynamicCaptionsModalContentProps {
  rows: DynamicCaptionRow[]
  generatingTargets: Partial<Record<number, boolean>>
  updatingProviders: Partial<Record<DynamicCaptionProvider, boolean>>
  isSaving: boolean
  isGeneratingAnyDynamicCaption: boolean
  onToggleProvider: (provider: DynamicCaptionProvider, isChecked: boolean) => void
  onRegenerate: (provider: DynamicCaptionProvider, targetId: number) => void
  onChangeCaption: (targetId: number, value: string) => void
  getDynamicCaptionValue: (target: ScheduledPostTarget) => string
  getCharacterStats: (provider: DynamicCaptionProvider, caption: string) => CharacterStats
  setDynamicCaptionRef: (targetId: number, element: HTMLTextAreaElement | null) => void
  connect: (popup?: Window) => void
  locationsByTargetId: Partial<Record<number, PostLocation | null>>
  onChangeLocation: (targetId: number, location: PostLocation | null) => void
  tagsByTargetId: Partial<Record<number, PostTag[]>>
  onChangeTags: (targetId: number, tags: PostTag[]) => void
  isCarousel: boolean
  isVideo: boolean
}

export const DynamicCaptionsModalContent = ({
  rows,
  generatingTargets,
  updatingProviders,
  isSaving,
  isGeneratingAnyDynamicCaption,
  onToggleProvider,
  onRegenerate,
  onChangeCaption,
  getDynamicCaptionValue,
  getCharacterStats,
  setDynamicCaptionRef,
  connect,
  locationsByTargetId,
  onChangeLocation,
  tagsByTargetId,
  onChangeTags,
  isCarousel,
  isVideo,
}: DynamicCaptionsModalContentProps) => {
  const isLocationSupportedForX = useFlipperFlag('location_tagging_x')
  const isLocationSupportedForLinkedIn = useFlipperFlag('location_tagging_linkedin')
  const isLocationSupportedForFacebook = useFlipperFlag('location_tagging_facebook')
  const isLocationSupportedForInstagram = useFlipperFlag('location_tagging_instagram')
  const isTaggingSupportedForX = useFlipperFlag('user_tagging_x')
  const isTaggingSupportedForInstagram = useFlipperFlag('user_tagging_instagram')
  const isMentioningSupportedForX = useFlipperFlag('user_mentioning_x')
  const isMentioningSupportedForLinkedIn = useFlipperFlag('user_mentioning_linkedin')
  const isMentioningSupportedForFacebook = useFlipperFlag('user_mentioning_facebook')
  const isMentioningSupportedForInstagram = useFlipperFlag('user_mentioning_instagram')

  const isLocationEnabled = (provider: ExternalIntegrationEnum) => {
    switch (provider) {
      case ExternalIntegrationEnum.X:
        return isLocationSupportedForX
      case ExternalIntegrationEnum.LinkedIn:
        return isLocationSupportedForLinkedIn
      case ExternalIntegrationEnum.Facebook:
        return isLocationSupportedForFacebook
      case ExternalIntegrationEnum.Instagram:
        return isLocationSupportedForInstagram
      default:
        return false
    }
  }

  const isTaggingEnabled = (provider: ExternalIntegrationEnum) => {
    switch (provider) {
      case ExternalIntegrationEnum.X:
        return isTaggingSupportedForX
      case ExternalIntegrationEnum.Instagram:
        return isTaggingSupportedForInstagram
      default:
        return false
    }
  }

  const isMentioningEnabled = (provider: ExternalIntegrationEnum, accountType?: string) => {
    switch (provider) {
      case ExternalIntegrationEnum.X:
        return isMentioningSupportedForX
      case ExternalIntegrationEnum.LinkedIn:
        return isMentioningSupportedForLinkedIn && accountType === 'page'
      case ExternalIntegrationEnum.Facebook:
        return isMentioningSupportedForFacebook && accountType === 'page'
      case ExternalIntegrationEnum.Instagram:
        return isMentioningSupportedForInstagram
      default:
        return false
    }
  }

  return (
    <div className={Styles.dynamicContent}>
      <div className={Styles.dynamicGrid}>
        {rows.map((row) => {
          const isUpdating = !!updatingProviders[row.provider]

          return (
            <div key={row.provider} className={Styles.channelColumn}>
              <div className={Styles.channelHeader}>
                <div className={Styles.channelTitleGroup}>
                  <div className={Styles.channelTitleRow}>
                    <PlatformIcon platform={row.provider} size={20} isBrand />
                    <span className={Styles.channelTitle}>{row.label}</span>
                  </div>
                </div>
                <div className={Styles.channelActions}>
                  {!row.isConnected ? (
                    <InitiateOauthTrigger provider={row.provider} popUp onInitiatedOauth={connect}>
                      <Button className={Styles.connectButton} variant="secondary" size="sm">
                        Connect
                      </Button>
                    </InitiateOauthTrigger>
                  ) : (
                    <ToggleButton
                      name={`${row.provider}-caption-toggle`}
                      checked={row.isEnabled}
                      disabled={isSaving}
                      handleChange={() => onToggleProvider(row.provider, !row.isEnabled)}
                      size="sm-md"
                    />
                  )}
                </div>
              </div>
              <div className={cx(Styles.channelDescription)}>
                <Text variant="placeholder" color="var(--dark-60)">
                  {row.description}
                </Text>
              </div>
              <div className={Styles.captionInputs}>
                {row.accountCaptionItems.map((item) => {
                  const target = item.target
                  const captionValue = target ? getDynamicCaptionValue(target) : row.initialCaption
                  const { count, limit, isOverLimit } = getCharacterStats(row.provider, captionValue)
                  const isGenerating = target ? !!generatingTargets[target.id] : false
                  const useCompactTextarea = row.accountCaptionItems.length > 1
                  const dataTestId = target
                    ? `${row.provider}-caption-group-${target.id}`
                    : `${row.provider}-caption-group-placeholder`

                  return (
                    <div key={item.key} className={Styles.accountCaptionGroup} data-testid={dataTestId}>
                      <div className={cx(Styles.captionInput)}>
                        <div className={Styles.accountCaptionHeader}>
                          <div className={Styles.accountCaptionIdentity}>
                            {row.hasAccountIcon && row.isConnected && (
                              <>
                                <Avatar size={20} label={getInitials(item.accountName)} url={item.accountAvatar} />
                                <Text variant="secondary" color="var(--dark-80)">
                                  {item.accountName}
                                </Text>
                              </>
                            )}
                          </div>
                          <IconButton
                            icon={ArrowRefresh2}
                            variant="ghost-tertiary"
                            size="sm"
                            title={`Regenerate ${item.accountName}`}
                            onPress={() => target && onRegenerate(row.provider, target.id)}
                            isDisabled={!target || isGenerating || isSaving}
                          />
                        </div>
                        <div className={Styles.inputContainer}>
                          {isMentioningEnabled(row.provider, item.accountType) &&
                          item.isIntegrationActive !== false &&
                          target &&
                          MENTIONS_SUPPORTED_PROVIDERS.has(row.provider) ? (
                            <MentionableTextarea
                              name={`${row.provider}-caption-${target.id}`}
                              value={captionValue}
                              onValueChange={(newValue) => onChangeCaption(target.id, newValue)}
                              integrationId={target.external_integration_id}
                              provider={row.provider}
                              isValid={!isOverLimit}
                              rows={useCompactTextarea ? 5 : 10}
                              maxHeight={useCompactTextarea ? 200 : 345}
                              disabled={isSaving}
                              inputRef={(element) =>
                                setDynamicCaptionRef(target.id, element as HTMLTextAreaElement | null)
                              }
                              className={cx(Styles.dynamicTextarea, {
                                [Styles.accountTextarea]: useCompactTextarea,
                                [Styles.hiddenInput]: isGenerating || isUpdating,
                                [Styles.overLimitInput]: isOverLimit,
                              })}
                            />
                          ) : (
                            <Input
                              name={target ? `${row.provider}-caption-${target.id}` : `${row.provider}-caption`}
                              value={captionValue}
                              isTextarea
                              isValid={!isOverLimit}
                              rows={useCompactTextarea ? 5 : 10}
                              maxHeight={useCompactTextarea ? 200 : 345}
                              disabled={isSaving || !target}
                              onChange={(event) => {
                                if (!target) return
                                onChangeCaption(target.id, event.target.value)
                              }}
                              ref={(element) => {
                                if (!target) return
                                setDynamicCaptionRef(target.id, element as HTMLTextAreaElement | null)
                              }}
                              className={cx(Styles.dynamicTextarea, {
                                [Styles.accountTextarea]: useCompactTextarea,
                                [Styles.hiddenInput]: isGenerating || isUpdating,
                                [Styles.overLimitInput]: isOverLimit,
                              })}
                            />
                          )}
                          {(isGenerating || isUpdating) && (
                            <div className={Styles.skeletonOverlay}>
                              <LineSkeleton className={Styles.skeletonLine} width="85%" />
                              <LineSkeleton className={Styles.skeletonLine} width="75%" />
                              <LineSkeleton className={Styles.skeletonLine} width="90%" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className={cx(Styles.maxCharacters, {
                          [Styles.overLimit]: isOverLimit,
                        })}
                      >
                        Character Count: {count}/{limit}
                      </div>
                      {isLocationEnabled(row.provider) &&
                        item.isIntegrationActive !== false &&
                        target &&
                        target.external_integration_id &&
                        LOCATION_SUPPORTED_PROVIDERS.has(row.provider) &&
                        (row.provider !== ExternalIntegrationEnum.LinkedIn || item.accountType === 'page') &&
                        (row.provider !== ExternalIntegrationEnum.Facebook || item.accountType === 'page') &&
                        !(row.provider === ExternalIntegrationEnum.Facebook && isVideo) && (
                          <div className={Styles.locationSection}>
                            <LocationPicker
                              integrationId={target.external_integration_id}
                              provider={row.provider}
                              location={locationsByTargetId[target.id] ?? null}
                              onLocationChange={(location) => onChangeLocation(target.id, location)}
                              disabled={isSaving}
                            />
                          </div>
                        )}
                      {isTaggingEnabled(row.provider) &&
                        item.isIntegrationActive !== false &&
                        target &&
                        target.external_integration_id &&
                        TAGS_SUPPORTED_PROVIDERS.has(row.provider) &&
                        !isCarousel && (
                          <TagPicker
                            integrationId={target.external_integration_id}
                            tags={tagsByTargetId[target.id] ?? []}
                            onTagsChange={(tags) => onChangeTags(target.id, tags)}
                            disabled={isSaving}
                            maxTags={MAX_TAGS_BY_PROVIDER[row.provider]}
                          />
                        )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
